import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { resolveClientDisplayNameFromTicket } from '../../lib/clientDisplayName';
import { resolveInternalAssigneeDisplay } from '../../lib/ticketAssigneeDisplay';
import InternalAssigneeIdentity from '../../components/tickets/components/InternalAssigneeIdentity';

import type { AppDispatch } from '../../store';
import { useAuth } from '../../context/AuthProvider';

/** Tickets store */
import {
  fetchTickets,
  assignTicket,
  selectAllTickets,
  selectListLoading,
} from '../../store/features/tickets/ticketsSlice';
import { openTicketPortal } from '../../store/features/ticketPortal/ticketPortalSlice';
// import type { RootState } from '../../store';
/** Projects store (for Project column names) */
import {
  fetchProjects,
  selectAllProjects,
  fetchProjectsByIds,
} from '../../store/features/projects/projectsSlice';

/* ────────── DEBUG helpers ────────── */
const DEBUG = true;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dlog = (...args: any[]) => { if (DEBUG) console.log('[TicketQueuePage]', ...args); };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dgroup = (label: string, cb: () => void) => { if (!DEBUG) return; console.group(`[TicketQueuePage] ${label}`); try { cb(); } finally { console.groupEnd(); } };

/* ────────── Animations ────────── */
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

/* ────────── Layout (light header + dark table card to match client) ────────── */
const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Heading = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.7rem);
  margin: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-weight: 700;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
`;

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .2px;
  text-transform: uppercase;
  color: #9AA4B2;
`;

const FilterSelect = styled.select`
  padding: 10px 36px 10px 12px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 12px;
  color: #E8EAED;
  background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.12);
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23cbd2e1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  color-scheme: dark;
  &:focus { outline: 2px solid #8ab4f8; outline-offset: 2px; }
  option { background: #12161b; color: #E8EAED; }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StatCard = styled(motion.div)`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,.06);
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  color: #E8EAED;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const StatLabel = styled.div`
  color: #9AA4B2;
  font-size: 0.9rem;
  font-weight: 800;
`;

/* — Dark card table (mirrors client TicketsTable) — */
const CardWrap = styled.div`
  padding: 0;
  overflow: hidden;

  display: grid;
  grid-template-rows: auto 1fr;
  height: clamp(360px, 60vh, 640px);

  border-radius: 14px;
  border: 1px solid rgba(255,255,255,.06);
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  box-shadow: 0 18px 48px rgba(0,0,0,.45);
  color: #E8EAED;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
`;
const Title = styled.div`
  font-weight: 900;
  letter-spacing: .3px;
  color: #E8EAED;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const Scroll = styled.div`
  overflow: auto;
  color-scheme: dark;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const DesktopOnly = styled.div`
  display: block;
  @media (max-width: 767px) { display: none; }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  color: #E8EAED;
  font-size: 14px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-weight: 900;
    letter-spacing: .3px;
    background: #1f2430;
    border-bottom: 1px solid rgba(255,255,255,.10);
  }

  tbody td { border-bottom: 1px solid rgba(255,255,255,.08); }
  tbody tr:hover td { background: rgba(255,255,255,.03); cursor: pointer; }
`;

const StatusPill = styled.span<{ $bg: string }>`
  padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 800;
  color: #111827; background: ${({ $bg }) => $bg};
`;

const Dot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 767px) { display: block; }
`;
const MobileList = styled.div` display: grid; `;
const RowCard = styled.button`
  all: unset;
  width: 100%;
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  cursor: pointer;
  &:active { background: rgba(255,255,255,.04); }
`;
const RowTitle = styled.div`
  font-weight: 800; color: #E8EAED;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const RowMeta = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  color: #cbd2e1; font-size: 12px; font-weight: 700;
`;
const Pill = styled.span`
  display: inline-grid; grid-auto-flow: column; align-items: center; gap: 8px;
  padding: 4px 8px; border-radius: 999px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
`;

const AssignBtn = styled.button`
  all: unset;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.06);
  color: #E8EAED;
  font-weight: 800;
  cursor: pointer;
  transition: background .2s ease;
  &:hover { background: rgba(255,255,255,.10); }
  &:disabled { opacity: .45; cursor: default; }
`;

const EmptyState = styled.div`
  padding: 24px;
  color: #cbd2e1;
`;

const SkeletonBar = styled.div<{ $width?: string; $height?: string; $rounded?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: ${({ $rounded }) => $rounded || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255,255,255,.06) 0%,
    rgba(255,255,255,.12) 50%,
    rgba(255,255,255,.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.35s linear infinite;
`;

const SkeletonPill = styled(SkeletonBar)`
  height: 24px;
  border-radius: 999px;
`;

const SkeletonCellStack = styled.div`
  display: grid;
  gap: 8px;
`;

/* ────────── Category taxonomy (derive from tags) ────────── */
type CategoryKey =
  | 'bug'
  | 'feature'
  | 'support'
  | 'billing'
  | 'onboarding'
  | 'infra'
  | 'analytics'
  | 'design'
  | 'content'
  | 'other'
  | 'general';

const CATEGORY_DEFS: Array<{
  key: CategoryKey;
  label: string;
  icon: string;
  matchTags: string[]; // lowercase
}> = [
    { key: 'bug', label: 'Bug', icon: '🐛', matchTags: ['bug', 'defect', 'fix'] },
    { key: 'feature', label: 'Feature', icon: '✨', matchTags: ['feature', 'enhancement'] },
    { key: 'support', label: 'Support', icon: '🤝', matchTags: ['support', 'helpdesk'] },
    { key: 'billing', label: 'Billing', icon: '💳', matchTags: ['billing', 'invoice', 'payment'] },
    { key: 'onboarding', label: 'Onboarding', icon: '🚀', matchTags: ['onboarding', 'setup'] },
    { key: 'infra', label: 'Infrastructure', icon: '🛠️', matchTags: ['infra', 'ops', 'devops'] },
    { key: 'analytics', label: 'Analytics', icon: '📈', matchTags: ['analytics', 'reporting', 'metrics'] },
    { key: 'design', label: 'Design', icon: '🎨', matchTags: ['design', 'ux', 'ui'] },
    { key: 'content', label: 'Content', icon: '📝', matchTags: ['content', 'docs', 'documentation'] },
    { key: 'other', label: 'Other', icon: '📦', matchTags: ['other', 'misc'] },
  ];

function deriveCategory(tags?: string[] | null): CategoryKey {
  const pool = (tags ?? []).map(t => t.toLowerCase());
  for (const def of CATEGORY_DEFS) {
    if (def.matchTags.some(mt => pool.includes(mt))) return def.key;
  }
  return 'general';
}
function categoryLabel(key: CategoryKey) {
  const def = CATEGORY_DEFS.find(d => d.key === key);
  return def ? `${def.icon} ${def.label}` : 'General';
}

/* ────────── Misc helpers ────────── */
// Robust timestamp helpers (same as agent page)
function tsToDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate() as Date;
  const s = value?.seconds ?? value?._seconds;
  if (typeof s === 'number') return new Date(s * 1000);
  if (typeof value === 'number') return new Date(value > 1e12 ? value : value * 1000);
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}
function formatYMD(d: Date | null) {
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

type UiStatus = 'submitted' | 'in_progress' | 'blocked' | 'resolved' | 'closed' | 'triage';
function mapStatusToUi(s?: string): UiStatus {
  switch (s) {
    case 'in_progress': return 'in_progress';
    case 'blocked': return 'blocked';
    case 'resolved': return 'resolved';
    case 'closed': return 'closed';
    case 'open':
    default: return 'submitted';
  }
}
function toneFor(s: UiStatus) {
  switch (s) {
    case 'in_progress': return '#A7F3D0';
    case 'blocked': return '#FDE68A';
    case 'resolved': return '#93C5FD';
    case 'submitted': return '#E5E7EB';
    case 'triage': return '#FCA5A5';
    case 'closed': return '#D1D5DB';
    default: return '#E5E7EB';
  }
}

// drawer priority mapping
type UiPriority = 'urgent' | 'high' | 'medium' | 'low';
function mapPriorityToUi(p?: string): UiPriority {
  switch (p) {
    case 'P0': return 'urgent';
    case 'P1': return 'high';
    case 'P3': return 'low';
    case 'P2':
    default: return 'medium';
  }
}


function renderTitle(raw: unknown) {
  const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim();
  return s ? s : <em style={{ color: '#9aa4b2' }}>Untitled ticket</em>;
}

function renderTicketId(ticketNumberRaw?: unknown) {
  const ticketNumber = String(ticketNumberRaw ?? '').trim();
  if (!ticketNumber) return '--';
  return <span style={{ color: '#8DEBFF', fontWeight: 900 }}>#{ticketNumber}</span>;
}

/* ────────── Page ────────── */
type StatusFilter =
  | 'all'
  | 'open'
  | 'in_progress'
  | 'blocked'
  | 'resolved'
  | 'closed';
type CategoryFilter = 'all' | string;
type TeamFilter = 'all' | string;

export default function TicketQueuePage() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();

  const ticketsRaw = useSelector(selectAllTickets);
  const loading = useSelector(selectListLoading);
  const projects = useSelector(selectAllProjects);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all');
  const openViewer = React.useCallback((id: string) => {
    dispatch(openTicketPortal({ ticketId: id }));
  }, [dispatch]);

  function toBackendStatusParam(f: 'all' | 'open' | 'in_progress' | 'blocked' | 'resolved' | 'closed') {
    if (f === 'all') {
      // Show *all* states server-side. If you prefer only active on "All", use only the first three.
      return 'open,in_progress,blocked,resolved,closed';
    }
    return f;
  }


  /* ────────── DEBUG: initial load + fetch echo ────────── */
  useEffect(() => {
    dgroup('MOUNT', () => {
      dlog('user', { uid: user?.uid, email: user?.email });
    });

    (async () => {
      dlog('dispatch(fetchProjects)');
      await dispatch(fetchProjects());
      const params = {
        unassigned: true,
        orderBy: 'createdAt',
        direction: 'desc',
        limit: 200,
        status: toBackendStatusParam('all'), // pull everything at first load
      } as any;

      dgroup('FETCH begin', () => {
        dlog('params', params);
      });

      try {
        const payload = await dispatch(fetchTickets(params)).unwrap();
        dgroup('FETCH done', () => {
          dlog('payload.length', Array.isArray(payload) ? payload.length : '(not array)');
          if (Array.isArray(payload) && payload.length) {
            console.table(
              payload.map((t: any) => ({
                id: t.id,
                title: t.title,
                status: t.status,
                assignedAgentId: t.assignedAgentId ?? null,
                createdAt: t.createdAt?._seconds ?? t.createdAt?.seconds ?? null,
                updatedAt: t.updatedAt?._seconds ?? t.updatedAt?.seconds ?? null,
                teamId: t.teamId ?? null,
                projectId: t.projectId ?? null,
              }))
            );
          } else {
            dlog('payload', payload);
          }
        });
      } catch (err) {
        console.error('[TicketQueuePage] fetchTickets error', err);
      }
    })();
    // we only want this on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);


  useEffect(() => {
    const params = {
      unassigned: true,
      orderBy: 'createdAt',
      direction: 'desc',
      limit: 200,
      status: toBackendStatusParam(statusFilter),
    } as any;
    dispatch(fetchTickets(params));
  }, [dispatch, statusFilter]);


  /* ────────── DEBUG: whenever Redux tickets change ────────── */
  useEffect(() => {
    dgroup('REDUX ticketsRaw changed', () => {
      dlog('count', ticketsRaw.length);
      if (ticketsRaw.length) {
        console.table(
          ticketsRaw.map((t: any) => ({
            id: t.id,
            title: t.title,
            status: t.status,
            assignedAgentId: t.assignedAgentId ?? null,
            createdAt: t.createdAt?._seconds ?? t.createdAt?.seconds ?? null,
            updatedAt: t.updatedAt?._seconds ?? t.updatedAt?.seconds ?? null,
            teamId: t.teamId ?? null,
            projectId: t.projectId ?? null,
          }))
        );
      } else {
        dlog('ticketsRaw is empty []');
      }
      // Expose a quick handle for manual inspection in the browser
      (window as any).__ticketsRaw = ticketsRaw;
    });
  }, [ticketsRaw]);

  /* ────────── DEBUG: projects snapshot ────────── */
  useEffect(() => {
    dgroup('PROJECTS snapshot', () => {
      dlog('count', projects.length);
      (window as any).__projects = projects;
    });
  }, [projects]);



  type Row = {
    id: string;
    ticketNumber?: string | null;
    title: string;
    priority: UiPriority;
    tags: string[];
    categoryToken: string;    // planId or legacy key
    categoryText: string;     // planId as-is, or "✨ Feature" etc.
    hasServerCategory: boolean;
    teamLabel: string;
    status: UiStatus;
    projectId: string;
    clientLabel: string;

    assignedAgentId: string | null;
    assignedAgentLabel: string;
    assignedAgentDisplayName: string | null;
    assignedAgentFirstName: string | null;
    assignedAgentLastName: string | null;
    assignedAgentEmail: string | null;
    assignedAgentPhotoURL: string | null;
    updatedAt: Date | null;           // ← NEW: store Date
    createdAt: Date | null;           // ← NEW: fallback for display
    slaDue: any;
  };


  // map to UI rows
  const rows = useMemo<Row[]>(() => {
    const rr = ticketsRaw.map((t: any) => {
      const serverCat = (t as any).category as string | undefined; // planId set by backend
      const hasServerCategory = Boolean(serverCat && serverCat.trim());
      const derivedKey = deriveCategory((t as any).tags);           // legacy fallback

      const categoryToken = hasServerCategory ? String(serverCat) : derivedKey;
      const categoryText = hasServerCategory ? String(serverCat) : categoryLabel(derivedKey);

      return {
        id: t.id,
        ticketNumber: (t as any).ticketNumber ?? null,
        title: t.title,
        priority: mapPriorityToUi(String(t.priority)),
        tags: (t as any).tags ?? [],
        categoryToken,
        categoryText,
        hasServerCategory,
        teamLabel:
          String((t as any).currentTeamLabel ?? '').trim() ||
          String((t as any).defaultTeamLabel ?? '').trim() ||
          String((t as any).currentTeamNodeId ?? '').trim() ||
          String((t as any).owningTeamNodeId ?? '').trim() ||
          String((t as any).teamId ?? '').trim() ||
          'Unassigned',
        status: mapStatusToUi(String(t.status)),
        projectId: String(t.projectId ?? ''), 
        clientLabel: resolveClientDisplayNameFromTicket(t, '--'),
        assignedAgentId: t.assignedAgentId ?? null,
        assignedAgentLabel: resolveInternalAssigneeDisplay(t, ''),
        assignedAgentDisplayName: t.assignedAgentDisplayName ?? null,
        assignedAgentFirstName: t.assignedAgentFirstName ?? null,
        assignedAgentLastName: t.assignedAgentLastName ?? null,
        assignedAgentEmail: t.assignedAgentEmail ?? null,
        assignedAgentPhotoURL: t.assignedAgentPhotoURL ?? null,
        updatedAt: tsToDate(t.updatedAt ?? null),
     createdAt: tsToDate(t.createdAt ?? null),
        slaDue: (t as any).plannedEndAt ?? (t as any).slaResolutionDueAt ?? null,
      };
    });
    dgroup('ROWS recompute', () => {
      dlog('rows.length', rr.length);
      (window as any).__rows = rr;
    });
    return rr;
  }, [ticketsRaw]);



  const categoryOptions = useMemo(() => {
    const m = new Map<string, string>(); // token -> label
    rows.forEach(r => m.set(r.categoryToken, r.categoryText));
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const teamOptions = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.teamLabel).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  }, [rows]);



  // filters
  const filtered = useMemo(() => {
    const out = rows.filter(r => {
      const statusOk =
        statusFilter === 'all'
          ? true
          : (statusFilter === 'open' ? r.status === 'submitted' : mapStatusToUi(statusFilter) === r.status);

      const categoryOk =
        categoryFilter === 'all' ? true : r.categoryToken === categoryFilter;

      const teamOk =
        teamFilter === 'all' ? true : r.teamLabel === teamFilter;

      return statusOk && categoryOk && teamOk;
    });
    dgroup('FILTER result', () => {
      dlog('statusFilter', statusFilter, 'categoryFilter', categoryFilter, 'teamFilter', teamFilter, 'count', out.length);
      (window as any).__filtered = out;
    });
    return out;
  }, [rows, statusFilter, categoryFilter, teamFilter]);

  // stats from current list
  const stats = useMemo(() => {
    const total = rows.length;
    const open = rows.filter(r => r.status === 'submitted').length;
    const inProgress = rows.filter(r => r.status === 'in_progress').length;
    const unassigned = rows.filter(r => !r.assignedAgentId).length;
    return { total, open, inProgress, unassigned };
  }, [rows]);

  const byProject = useMemo(
   () => new Map(projects.map(p => [String(p.id), p])),
   [projects]
 );



 useEffect(() => {
   const wanted = Array.from(new Set(rows.map(r => r.projectId).filter(Boolean)));
   if (!wanted.length) return;
   const have = new Set(projects.map(p => String(p.id)));
   const missing = wanted.filter(id => !have.has(id));
   if (missing.length) {
     dgroup('MISSING PROJECTS fetch', () => dlog({ missing }));
     dispatch(fetchProjectsByIds(missing));
   }
 }, [dispatch, rows, projects]);

  const handleAssignSelf = async (ticketId: string) => {
    if (!user?.uid) return;
    dlog('Assign to me clicked', { ticketId, uid: user.uid });
    try {
      await dispatch(assignTicket({ id: ticketId, agentId: user.uid })).unwrap();
      dlog('Assign OK, refetching…');
      await dispatch(fetchTickets({
        unassigned: true,
        orderBy: 'createdAt',
        direction: 'asc',
        limit: 200
      } as any)).unwrap();
      dlog('Refetch done');
    } catch (e) {
      console.warn('Assign failed', e);
    }
  };

  return (
    <Wrap>
      <Header>
        <Heading>Global Ticket Queue</Heading>
        <FilterContainer>
          <FilterGroup>
            <FilterLabel>Status:</FilterLabel>
            <FilterSelect
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blockers</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Category:</FilterLabel>
            <FilterSelect
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as CategoryFilter)}
            >
              <option value="all">All</option>
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </FilterSelect>

          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Team:</FilterLabel>
            <FilterSelect
              value={teamFilter}
              onChange={e => setTeamFilter(e.target.value as TeamFilter)}
            >
              <option value="all">All</option>
              {teamOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        </FilterContainer>
      </Header>

      <Stats>
        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total Tickets</StatLabel>
        </StatCard>
        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatNumber>{stats.open}</StatNumber>
          <StatLabel>Open</StatLabel>
        </StatCard>
        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatNumber>{stats.inProgress}</StatNumber>
          <StatLabel>In Progress</StatLabel>
        </StatCard>
        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatNumber>{stats.unassigned}</StatNumber>
          <StatLabel>Unassigned</StatLabel>
        </StatCard>
      </Stats>

      {/* Client-table look, with Category column + Assign action */}
      <CardWrap as={motion.div} variants={fadeInUp} initial="hidden" animate="visible">
        <TopBar>
          <Title>All projects</Title>
        </TopBar>

        <Scroll>
          {loading ? (
            <>
              <DesktopOnly>
                <Table>
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Project</th>
                      <th>Client</th>
                      <th>Assigned</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 7 }).map((_, idx) => (
                      <tr key={`ticket-queue-skeleton-${idx}`}>
                        <td><SkeletonBar $width="96px" $height="16px" /></td>
                        <td>
                          <SkeletonCellStack>
                            <SkeletonBar $width="220px" />
                            <SkeletonBar $width="140px" $height="12px" />
                          </SkeletonCellStack>
                        </td>
                        <td><SkeletonPill $width="96px" /></td>
                        <td>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <SkeletonBar $width="8px" $height="8px" $rounded="999px" />
                            <SkeletonBar $width="74px" />
                          </div>
                        </td>
                        <td><SkeletonBar $width="128px" /></td>
                        <td><SkeletonBar $width="132px" /></td>
                        <td><SkeletonPill $width="110px" /></td>
                        <td><SkeletonBar $width="84px" /></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DesktopOnly>

              <MobileOnly>
                <MobileList>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <RowCard
                      as="div"
                      key={`ticket-queue-skeleton-mobile-${idx}`}
                      style={{ cursor: 'default' }}
                    >
                      <SkeletonCellStack>
                        <SkeletonBar $width="72%" />
                        <SkeletonBar $width="48%" $height="12px" />
                      </SkeletonCellStack>
                      <RowMeta>
                        <SkeletonPill $width="86px" />
                        <SkeletonPill $width="92px" />
                        <SkeletonPill $width="88px" />
                      </RowMeta>
                    </RowCard>
                  ))}
                </MobileList>
              </MobileOnly>
            </>
          ) : filtered.length === 0 ? (
            <EmptyState>No tickets found. Try adjusting filters.</EmptyState>
          ) : (
            <>
              {/* Desktop table */}
              <DesktopOnly>
                <Table>
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Project</th>
                      <th>Client</th>
                      <th>Assigned</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.id} onClick={() => openViewer(t.id)}>
                        <td>{renderTicketId((t as any).ticketNumber)}</td>
                        <td>{renderTitle(t.title)}</td>
                        <td><StatusPill $bg={toneFor(t.status)}>{t.status.replace(/_/g, ' ')}</StatusPill></td>
                        <td>
                          <Dot $color={t.priority === 'urgent' ? '#EF4444' : t.priority === 'high' ? '#F59E0B' : t.priority === 'medium' ? '#22D3EE' : '#9CA3AF'} />{' '}
                          <span style={{ marginLeft: 8 }}>{t.priority}</span>
                        </td>
                        <td>{byProject.get(t.projectId)?.name ?? '—'}</td>
                        <td>{t.clientLabel}</td>
                        <td>
                          {t.assignedAgentLabel ? (
                            <InternalAssigneeIdentity source={t} />
                          ) : (
                            <AssignBtn onClick={(e) => { e.stopPropagation(); handleAssignSelf(t.id); }}>

                              Assign to me
                            </AssignBtn>
                          )}
                        </td>
                         <td title={(t.updatedAt ?? t.createdAt)?.toLocaleString?.() || ''}>
                         {formatYMD(t.updatedAt ?? t.createdAt)}
                       </td>
                      
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DesktopOnly>

              {/* Mobile list */}
              <MobileOnly>
                <MobileList>
                  {filtered.map(t => (
                    <RowCard key={t.id} onClick={() => openViewer(t.id)}>
                       <RowTitle>{renderTitle(t.title)}</RowTitle>
                      <RowMeta>
                        <Pill title="Ticket ID">{renderTicketId((t as any).ticketNumber)}</Pill>
                        <StatusPill $bg={toneFor(t.status)}>{t.status.replace(/_/g, ' ')}</StatusPill>
                        <Pill title="Priority">
                          <Dot $color={t.priority === 'urgent' ? '#EF4444' : t.priority === 'high' ? '#F59E0B' : t.priority === 'medium' ? '#22D3EE' : '#9CA3AF'} />
                          <span style={{ marginLeft: 6 }}>{t.priority}</span>
                        </Pill>
                        <Pill title="Project">{byProject.get(t.projectId)?.name ?? '—'}</Pill>
                        <Pill title="Client">{t.clientLabel}</Pill>
                        <span title={(t.updatedAt ?? t.createdAt)?.toLocaleString?.() || ''}>
                         {formatYMD(t.updatedAt ?? t.createdAt)}
                       </span>
                        
                        {t.assignedAgentLabel ? (
                          <Pill title="Assigned"><InternalAssigneeIdentity source={t} size={20} /></Pill>
                        ) : (
                          <Pill>Tap to assign</Pill>
                        )}
                      </RowMeta>
                    </RowCard>
                  ))}
                </MobileList>
              </MobileOnly>
            </>
          )}
        </Scroll>
      </CardWrap>
    </Wrap>
  );
}
